"use client"

import * as React from "react"
import { NotificationItem } from "@/types/notification"
import { NotificationItemCard } from "./NotificationItemCard"
import { AppIcon } from "@/components/desk/AppIcon"
import { ChevronDown, Trash2 } from "lucide-react"

interface AppNotificationGroupProps {
  appName: string
  items: NotificationItem[]
  onDelete: (id: number) => void
  onDeleteApp?: (appName: string) => void
}

export function AppNotificationGroup({ appName, items, onDelete, onDeleteApp }: AppNotificationGroupProps) {
  const [isHovered, setIsHovered] = React.useState(false)

  // Se houver apenas 1 notificação, exibe o cartão diretamente sem colapso
  if (items.length === 1) {
    return <NotificationItemCard item={items[0]} onDelete={onDelete} />
  }

  // Resolução da Logo do App no cabeçalho via AppIcon oficial
  const rawLogo = items.find(i => i.avatarUrl || i.appIcon)?.avatarUrl || items.find(i => i.avatarUrl || i.appIcon)?.appIcon
  const safeLogo = rawLogo && !rawLogo.includes("cdn.boldworkspace.com.br") ? rawLogo : undefined

  // Pegar até 3 cartões reais para a pilha 3D colapsada
  const card1 = items[0]
  const card2 = items[1]
  const card3 = items[2]

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative transition-all duration-500 ease-in-out group/stack"
    >
      {/* CABEÇALHO DO GRUPO DO APLICATIVO */}
      <div className="flex items-center justify-between px-1 mb-1.5">
        <div className="flex items-center gap-1.5">
          <AppIcon iconUrl={safeLogo} name={appName} size="small" />
          <span className="text-[11px] font-bold text-zinc-900 dark:text-zinc-100 uppercase tracking-wider font-mono">
            {appName}
          </span>
          <span className="text-[10px] font-bold px-1.5 py-0.2 rounded-full bg-blue-100 dark:bg-blue-950/60 text-[#004EB1] dark:text-blue-400">
            {items.length}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {onDeleteApp && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onDeleteApp(appName)
              }}
              className="text-[10px] font-medium text-zinc-400 hover:text-red-500 flex items-center gap-1 cursor-pointer transition-colors"
              title={`Limpar todas as notificações do ${appName}`}
            >
              <Trash2 className="size-3" />
              <span>Limpar app</span>
            </button>
          )}

          <span className="text-[10px] text-zinc-400 font-sans">
            {isHovered ? "Expansor ativo" : "Passe o mouse para expandir"}
          </span>
        </div>
      </div>

      {isHovered ? (
        // MODO EXPANDIDO (NO HOVER SULAVIZADO): Mostra TODAS as notificações com animação fluida de 500ms
        <div className="flex flex-col gap-2.5 animate-in fade-in zoom-in-95 duration-500 ease-in-out pl-2 border-l-2 border-[#004EB1]/40 dark:border-blue-400/40 my-1">
          {items.map((item) => (
            <NotificationItemCard key={item.id} item={item} onDelete={onDelete} />
          ))}
        </div>
      ) : (
        // MODO COLAPSADO 3D REAL (500ms Easing Suave): 2º e 3º cartão REAL sobressaindo na base
        <div className="relative pb-10 cursor-pointer group/cascade transition-all duration-500 ease-in-out">
          {/* Card 3 REAL (Notificação 3 - fundo) */}
          {card3 && (
            <div className="absolute inset-x-4 top-8 z-10 transform scale-[0.92] opacity-65 pointer-events-none transition-all duration-500 ease-in-out shadow-2xs">
              <NotificationItemCard item={card3} />
            </div>
          )}

          {/* Card 2 REAL (Notificação 2 - meio) */}
          {card2 && (
            <div className="absolute inset-x-2 top-4 z-20 transform scale-[0.96] opacity-85 pointer-events-none transition-all duration-500 ease-in-out shadow-sm">
              <NotificationItemCard item={card2} />
            </div>
          )}

          {/* Card 1 REAL (Notificação 1 - topo principal) */}
          <div className="relative z-30 transform scale-100 shadow-md rounded-xl transition-all duration-500 ease-in-out">
            <NotificationItemCard item={card1} onDelete={onDelete} />
          </div>

          {/* Badge Indicador Flutuante de Notificações em Cascata */}
          <div className="absolute right-3 -bottom-1 z-40 flex items-center gap-1.5 bg-[#004EB1] text-white text-[9px] font-bold px-2.5 py-0.5 rounded-full shadow-md">
            <span>+{items.length - 1} em cascata</span>
            <ChevronDown className="size-3" />
          </div>
        </div>
      )}
    </div>
  )
}
